'use client';

import { Box, Flex, Text } from '@chakra-ui/react';
import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import AssistantComposer from '@features/assistant/AssistantComposer';
import ModeSuggestionPanel from '@features/assistant/ModeSuggestionPanel';
import type { AssistantSurfaceConfig } from '@features/assistant/modeSuggestions';

const CHAT_MAX_WIDTH = '720px';
const DEFAULT_WORDMARK = 'risk';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const MessageBubble = memo(({ role, content }: ChatMessage) => (
  <Flex
    alignSelf={role === 'user' ? 'flex-end' : 'flex-start'}
    bg={role === 'user' ? 'surface.inverse' : 'surface.muted'}
    color={role === 'user' ? 'text.inverse' : 'text.primary'}
    px={4}
    py={2}
    borderRadius="xl"
    maxW="85%"
  >
    <Text fontSize="sm">{content}</Text>
  </Flex>
));
MessageBubble.displayName = 'MessageBubble';

interface AssistantSurfaceProps {
  config: AssistantSurfaceConfig;
  // Wordmark/greeting shown above the composer until the chat starts; defaults to
  // the brand so every surface gets the same fade-on-first-message treatment.
  heading?: string;
  // Strike through the trailing "risk" in the wordmark (e.g. nullify~~risk~~).
  strikeRisk?: boolean;
  // The page's informational content, rendered below the assistant in the same scroll flow.
  children?: ReactNode;
}

const AssistantSurface = ({
  config,
  heading = DEFAULT_WORDMARK,
  strikeRisk = true,
  children,
}: AssistantSurfaceProps) => {
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isHeadingRemoved, setIsHeadingRemoved] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);

  // Anchor the view to the bottom on each new message so the latest turn lands
  // just above the (sticky) composer rather than off-screen below it.
  useEffect(() => {
    if (messages.length === 0) {
      return;
    }
    threadEndRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [messages.length]);

  const handleSelectPrompt = useCallback((text: string) => {
    setPrompt(text);
  }, []);

  // Unmount the wordmark only after it has faded/collapsed, so it doesn't leave
  // a gap above the thread once the chat starts.
  const handleHeadingFaded = useCallback(() => {
    setIsHeadingRemoved(true);
  }, []);

  // No assistant backend yet: a submission just adds the user's turn to the
  // thread above the composer. Assistant replies arrive once the LLM is wired in.
  // The composer passes the current text so this stays free of `prompt`.
  const handleSubmit = useCallback((text: string) => {
    const trimmed = text.trim();
    if (trimmed !== '') {
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
      setPrompt('');
    }
  }, []);

  const hasMessages = messages.length > 0;
  const riskAt = strikeRisk ? heading.toLowerCase().lastIndexOf('risk') : -1;

  return (
    <Box h="full" w="full" minH={0} overflowY="auto">
      <Flex direction="column" alignItems="center" w="full" px={4} pt={{ base: 8, md: 16 }} pb={6}>
        <Flex direction="column" w="full" maxW={CHAT_MAX_WIDTH} gap={6}>
          {heading != null && !isHeadingRemoved ? (
            <Box
              alignSelf="center"
              overflow="hidden"
              opacity={hasMessages ? 0 : 1}
              maxH={hasMessages ? '0' : '200px'}
              transform={hasMessages ? 'translateY(-8px)' : 'none'}
              transition="opacity 250ms ease, max-height 300ms ease, transform 250ms ease"
              onTransitionEnd={hasMessages ? handleHeadingFaded : undefined}
            >
              <Text
                as="h1"
                fontSize={{ base: '4xl', md: '5xl' }}
                fontWeight="medium"
                letterSpacing="tight"
                color="text.primary"
              >
                {riskAt >= 0 ? (
                  <>
                    {heading.slice(0, riskAt)}
                    <Text as="span" textDecoration="line-through" textDecorationThickness="2px">
                      {heading.slice(riskAt)}
                    </Text>
                  </>
                ) : (
                  heading
                )}
              </Text>
            </Box>
          ) : null}
          {hasMessages ? (
            <Flex direction="column" gap={3}>
              {messages.map((message, i) => (
                <MessageBubble key={i} role={message.role} content={message.content} />
              ))}
            </Flex>
          ) : null}
          <Box
            position={hasMessages ? 'sticky' : 'static'}
            bottom={4}
            zIndex={1}
            bg="surface.background"
            pt={hasMessages ? 2 : 0}
          >
            <AssistantComposer
              value={prompt}
              onChange={setPrompt}
              onSubmit={handleSubmit}
              placeholder={config.composerPlaceholder}
            />
          </Box>
          {hasMessages ? <Box ref={threadEndRef} /> : null}
          {!hasMessages ? (
            <ModeSuggestionPanel
              panelTitle={config.panelTitle}
              categories={config.categories}
              onSelectPrompt={handleSelectPrompt}
            />
          ) : null}
        </Flex>
      </Flex>
      {children}
    </Box>
  );
};

export default memo(AssistantSurface);
